import { useCallback, useMemo, useRef, useState } from 'react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { buildExtractedImagesDownload, extractPdfImages } from './extractImagesPdfEngine';
import { zipImageFiles } from '../pdf-to-image/pdfToImageEngine';
import ExtractedImagesPreview from './ExtractedImagesPreview';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

export default function ExtractImagesPdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [preparingDownload, setPreparingDownload] = useState(false);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        if (!list.length) return;

        let hadError = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const f of list) {
                if (!(f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'))) {
                    hadError = true;
                    notifications.show({ title: t('common.error'), message: t('extractImagesPdf.errors.invalidType'), color: 'red' });
                    continue;
                }
                if (f.size > MAX_FILE_BYTES) {
                    hadError = true;
                    notifications.show({ title: t('common.error'), message: t('extractImagesPdf.errors.tooLarge'), color: 'red' });
                    continue;
                }
                next.push(f);
            }
            return next;
        });
        if (hadError) setError(t('extractImagesPdf.errors.failed'));
        else setError(null);
        setResults([]);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in.
    usePdfPaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviewIndex((prev) => (index < prev ? prev - 1 : Math.max(0, Math.min(prev, files.length - 2))));
        setResults([]);
        setError(null);
    };

    const clearResultsOnly = () => {
        setResults([]);
        setError(null);
        setProgress(null);
    };

    const clearAll = () => {
        setFiles([]);
        setResults([]);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
        setPreparingDownload(false);
    };

    const downloadResult = async (result) => {
        if (!result || preparingDownload) return;
        setPreparingDownload(true);
        try {
            const prepared = await buildExtractedImagesDownload(result);
            if (prepared?.blob) {
                downloadBlob(prepared.blob, prepared.name);
            }
        } finally {
            setPreparingDownload(false);
        }
    };

    const handleDownloadZip = async () => {
        if (!results.length || preparingDownload) return;
        setPreparingDownload(true);
        try {
            const items = [];
            for (const result of results) {
                const prepared = await buildExtractedImagesDownload(result);
                if (prepared?.blob) items.push({ name: prepared.name, blob: prepared.blob });
            }
            if (!items.length) return;
            const zip = await zipImageFiles(items);
            downloadBlob(zip, 'extracted-images.zip');
        } finally {
            setPreparingDownload(false);
        }
    };

    const handleExtract = async () => {
        if (!files.length || processing) return;

        recordToolUsage('pdf/extract-images', { category: 'pdf' });

        setProcessing(true);
        setResults([]);
        setProgress(null);
        setError(null);

        const done = [];
        // Names of the PDFs that failed, by reason — one bad PDF must not hide the images
        // that the others gave, so these only become a warning when something did extract.
        const failed = { password: [], noImages: [], other: [] };

        for (const file of files) {
            try {
                const result = await extractPdfImages(file, {
                    onProgress: (page, total) => {
                        setProgress({ page, total, fileName: file.name });
                    },
                    onPartial: (partial) => {
                        const base = file.name.replace(/\.pdf$/i, '');
                        setResults((prev) => {
                            const next = [...prev];
                            const idx = next.findIndex((item) => item.name.startsWith(base));
                            if (idx >= 0) next[idx] = partial;
                            else next.push(partial);
                            return next;
                        });
                    },
                });
                done.push(result);
            } catch (err) {
                if (err?.message === 'password') failed.password.push(file.name);
                else if (err?.message === 'noImages') failed.noImages.push(file.name);
                else failed.other.push(file.name);
            }
        }

        setResults(done);
        setProcessing(false);
        setProgress(null);

        const problems = [
            [failed.password, 'extractImagesPdf.errors.password'],
            [failed.noImages, 'extractImagesPdf.errors.noImages'],
            [failed.other, 'extractImagesPdf.errors.failed'],
        ].filter(([names]) => names.length);
        // Other PDFs gave images: the failures are a heads-up, not the outcome.
        const color = done.length ? 'yellow' : 'red';
        const title = done.length ? t('common.warning') : t('common.error');
        for (const [names, key] of problems) {
            const msg = `${t(key)}${files.length > 1 ? ` (${names.join(', ')})` : ''}`;
            notifications.show({ title, message: msg, color });
        }
        if (done.length) {
            notifications.show({ title: t('common.success'), message: t('extractImagesPdf.done'), color: 'green' });
        }
        // The panel shows the error only when there is nothing else to show.
        setError(!done.length && problems.length ? t(problems[0][1]) : null);
    };

    const canExtract = files.length > 0 && !processing;

    const progressLabel = progress
        ? t('extractImagesPdf.progress', {
            page: toBnDigits(progress.page),
            total: toBnDigits(progress.total),
        })
        : '';

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun: canExtract,
        processing,
        hasResult: results.length > 0,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);
    const statusText = getToolStatusText(status, t, {
        success: t('extractImagesPdf.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const activeResult = results[previewIndex] ?? results[0] ?? null;
    const successLabel = results.length > 1
        ? `${t('extractImagesPdf.resultTitle')} (${toBnDigits(results.length)})`
        : activeResult
            ? (activeResult.count === 1
                ? t('extractImagesPdf.resultSingle', { size: formatFileSize(activeResult.totalBytes) })
                : t('extractImagesPdf.resultZip', {
                    count: toBnDigits(activeResult.count),
                    size: formatFileSize(activeResult.totalBytes),
                }))
            : '';
    const progressPercent = progress?.total
        ? Math.round((progress.page / progress.total) * 100)
        : undefined;


    return (
        <ToolPageLayout
            slug="pdf/extract-images"
            subtitleKey="extractImagesPdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(files.length) })}
            showAdd={files.length > 0}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={files.length > 0 || results.length > 0 || error ? clearAll : undefined}
            matchPreviewHeight={files.length > 0}
            primaryAction={{
                label: t('extractImagesPdf.extract'),
                onClick: handleExtract,
                disabled: !canExtract || results.length > 0,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {files.length === 0 ? (
                        <PdfDropZone
                            title={t('extractImagesPdf.dropTitle')}
                            hint={t('extractImagesPdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain pr-1 [scrollbar-width:thin]">
                                {files.map((file, index) => (
                                    <PdfUploadedFile
                                        key={`${file.name}-${index}`}
                                        name={file.name}
                                        meta={formatFileSize(file.size)}
                                        selected={previewIndex === index}
                                        onSelect={() => setPreviewIndex(index)}
                                        onRemove={() => removeFile(index)}
                                        removeLabel={t('extractImagesPdf.removeFile')}
                                    />
                                ))}
                            </div>
                            <p className="flex-none text-xs text-zinc-500 dark:text-zinc-400">
                                {t('extractImagesPdf.hint')}
                            </p>
                        </div>
                    )}
                </>
            )}
            settings={files.length === 0 ? (
                <ToolCard title={t('extractImagesPdf.hint')}>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                        {t('extractImagesPdf.hint')}
                    </p>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={progressLabel || t('extractImagesPdf.extracting')}
                    progress={progressPercent}
                    error={error}
                    successLabel={successLabel}
                    downloadLabel={t('extractImagesPdf.download')}
                    onDownload={results.length === 1 && activeResult
                        ? () => { void downloadResult(activeResult); }
                        : results.length > 1
                            ? () => { void handleDownloadZip(); }
                            : undefined}
                    onEditAgain={results.length > 0 ? clearResultsOnly : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={results.length > 0 ? clearAll : undefined}
                    againLabel={t('extractImagesPdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('extractImagesPdf.extract')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={files.length === 0 && !error}
                    pdfPreview={files.length > 0 && !processing && results.length === 0}
                    fill={(files.length > 0 || results.length > 0) && !processing && !error}
                >
                    {(files.length > 0 || activeResult) && !error ? (
                        results.length > 0 ? (
                            <ExtractedImagesPreview
                                results={results}
                                activeIndex={previewIndex}
                            />
                        ) : files.length > 0 && !processing ? (
                            <PdfDocPreview
                                files={files}
                                activeIndex={previewIndex}
                                onActiveIndexChange={setPreviewIndex}
                            />
                        ) : null
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
